import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { User } from '@/types';
import styles from './W_SecurityTab.module.css';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { authApi } from '@/services/authApi';

interface W_SecurityTabProps {
  user: User;
  onDirtyChange?: (isDirty: boolean) => void;
}

export interface SecurityTabHandle {
  resetChanges: () => void;
  saveChanges: () => Promise<void>;
}

const areSecurityStatesEqual = (a: SecurityFormData, b: SecurityFormData) =>
  JSON.stringify(a) === JSON.stringify(b);

interface SecurityFormData {
  phone: string;
  email: string;
  password: string;
}

const SecurityTabComponent = (
  { user, onDirtyChange }: W_SecurityTabProps,
  ref: React.Ref<SecurityTabHandle>
) => {
  const isMobileWidth = useIsMobile(520);
  const { language } = useLanguage();
  const initialData = useMemo<SecurityFormData>(
    () => ({
      phone: user.contact.phone || '',
      email: user.contact.email || '',
      password: user.security.password || '',
    }),
    [user]
  );

  const [formData, setFormData] = useState<SecurityFormData>(initialData);
  const [baselineData, setBaselineData] =
    useState<SecurityFormData>(initialData);
  const [isInitialized, setIsInitialized] = useState(false);

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordNotification, setPasswordNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!isInitialized) {
      setFormData(initialData);
      setBaselineData(initialData);
      setIsInitialized(true);
    }
  }, [initialData, isInitialized]);

  const isDirty = useMemo(
    () => !areSecurityStatesEqual(formData, baselineData),
    [formData, baselineData]
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    return () => {
      onDirtyChange?.(false);
    };
  }, [onDirtyChange]);

  const updateField = (field: keyof SecurityFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetChanges = useCallback(() => {
    setFormData(baselineData);
  }, [baselineData]);

  const handleSaveChanges = useCallback(async () => {
    try {
      const { profileApi: profileApiService } = await import(
        '@/services/profileApi'
      );
      const updateData = {
        email: formData.email,
        phone: formData.phone || undefined,
      };
      const response = await profileApiService.updateProfile(updateData);
      if (response.success) {
        setBaselineData(formData);
      }
    } catch {
      // ошибка сохранения настроек безопасности
    }
  }, [formData]);

  const handlePasswordChange = useCallback(async () => {
    if (!currentPassword || !newPassword) return;
    setPasswordLoading(true);
    setPasswordNotification(null);
    try {
      const res = await authApi.passwordChange(currentPassword, newPassword);
      if (res.success) {
        setPasswordNotification({
          type: 'success',
          message:
            res.data?.detail ||
            getTranslation(language, 'profileSecurity.changePasswordSuccess'),
        });
        setCurrentPassword('');
        setNewPassword('');
        setShowPasswordForm(false);
      } else {
        setPasswordNotification({
          type: 'error',
          message:
            res.message ||
            getTranslation(language, 'profileSecurity.changePasswordError'),
        });
      }
    } catch (e: any) {
      const msg =
        e?.data?.detail ||
        e?.message ||
        getTranslation(language, 'profileSecurity.changePasswordError');
      setPasswordNotification({ type: 'error', message: msg });
    } finally {
      setPasswordLoading(false);
    }
  }, [currentPassword, newPassword, language]);

  useImperativeHandle(
    ref,
    () => ({
      resetChanges: handleResetChanges,
      saveChanges: handleSaveChanges,
    }),
    [handleResetChanges, handleSaveChanges]
  );

  return (
    <div className={styles.wrapper}>
      <A_Input
        type='phone'
        label={getTranslation(language, 'profileSecurity.phone')}
        background='gray'
        value={formData.phone}
        onChange={value => updateField('phone', value)}
      />
      <A_Input
        type='email'
        label={getTranslation(language, 'profileSecurity.email')}
        background='gray'
        value={formData.email}
        onChange={value => updateField('email', value)}
      />
      <div className={styles.password_section}>
        <div className={styles.password_wrapper}>
          <A_Input
            type='text'
            label={getTranslation(language, 'profileSecurity.password')}
            background='gray'
            value='••••••••'
            onChange={() => {}}
            disabled
          />
          <A_Button
            size='small'
            variant='secondary'
            fontSize='text-p-4'
            width={isMobileWidth ? '100%' : 'fit-content'}
            onClick={() => {
              setShowPasswordForm(prev => !prev);
              setCurrentPassword('');
              setNewPassword('');
              setPasswordNotification(null);
            }}
          >
            {getTranslation(language, 'profileSecurity.change')}
          </A_Button>
        </div>
        {showPasswordForm && (
          <div className={styles.password_form}>
            <A_Input
              type='password'
              label={getTranslation(
                language,
                'profileSecurity.currentPassword'
              )}
              background='gray'
              value={currentPassword}
              onChange={setCurrentPassword}
            />
            <A_Input
              type='password'
              label={getTranslation(language, 'profileSecurity.newPassword')}
              background='gray'
              value={newPassword}
              onChange={setNewPassword}
            />
            <div className={styles.password_actions}>
              <A_Button
                size='small'
                variant='primary'
                fontSize='text-p-4'
                width={isMobileWidth ? '100%' : 'fit-content'}
                disabled={passwordLoading || !currentPassword || !newPassword}
                onClick={handlePasswordChange}
              >
                {getTranslation(language, 'profileSecurity.save')}
              </A_Button>
              <A_Button
                size='small'
                variant='secondary'
                fontSize='text-p-4'
                width={isMobileWidth ? '100%' : 'fit-content'}
                onClick={() => {
                  setShowPasswordForm(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setPasswordNotification(null);
                }}
              >
                {getTranslation(language, 'profileSecurity.cancel')}
              </A_Button>
            </div>
          </div>
        )}
        {passwordNotification && (
          <A_Notification
            type={passwordNotification.type}
            message={passwordNotification.message}
            isVisible
            onClose={() => setPasswordNotification(null)}
          />
        )}
      </div>
    </div>
  );
};

export const W_SecurityTab = forwardRef<SecurityTabHandle, W_SecurityTabProps>(
  SecurityTabComponent
);
